import {
	IModel,
	db,
	IIdentifiable,
	enumOrDefault,
	VIPIp,
	IVIPIp,
	VIPIpEquivalence,
	IVIPIpEquivalence,
	IVIPIpEquivalenceOptions,
	VIPCertificateIp,
	IVIPCertificateIp,
	IVIPCertificateIpOptions,
	VIPCapability,
	IVIPCapability,
	IVIPCapabilityOptions,
} from "@/loader";

export enum VipIpStatus {
	active = "active",
	disabled = "trash",
}

export function vipIpStatusOrDefault(value: any, defaultValue: VipIpStatus = VipIpStatus.active): VipIpStatus {
	return <VipIpStatus>enumOrDefault(value, defaultValue, Object.values(VipIpStatus));
}

export function getVipIpStatusLabel(value: VipIpStatus): string {
	switch (value) {
		case VipIpStatus.active:
			return window.wp.i18n._x("Active", "IP", "vip-vue");
		case VipIpStatus.disabled:
			return window.wp.i18n._x("Disabled", "IP", "vip-vue");
		default:
			return window.wp.i18n._x("Unknown Status", "IP", "vip-vue");
	}
}

export function getVipIpStatusList(): { value: any, text: string }[] {
	return [
		{ value: "active", text: window.wp.i18n._x("Active", "IP", "vip-vue") },
		{ value: "trash", text: window.wp.i18n._x("Disabled", "IP", "vip-vue") },
	];
}

export type VIPIpFields = "id" | "fk_parent" | "exceptional" | "pipeline" | "code" | "status" | "name" | "description";

/**
 * IP Base Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPIpBase extends IIdentifiable {
	/** Id */
	id?: number;
	/** Parent ID */
	fk_parent?: number | null;
	/** Exceptional */
	exceptional?: boolean;
	/** Pipeline */
	pipeline?: boolean;
	/** Code */
	code?: number;
	/** Name */
	name?: string;
	/** Description */
	description?: string | null;
	/** Status */
	status?: VipIpStatus;

	/** Parent IP */
	parent?: IVIPIp | null;

	/** Child IPs */
	children?: IVIPIp[];
	/** IPs given by this IP */
	give?: IVIPIpEquivalence[];
	/** IPs giving this IP */
	given_by?: IVIPIpEquivalence[];
	/** Certificates */
	certificates?: IVIPCertificateIp[];
	/** Capabilities */
	capabilities?: IVIPCapability[];
}

/**
 * IP Base Options Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPIpOptions {
	/** Indicates whether the Id should be included or not. Default true. */
	id?: boolean;
	/** Indicates whether the Parent ID should be included or not. Default true. */
	fk_parent?: boolean;
	/** Indicates whether the Exceptional should be included or not. Default true. */
	exceptional?: boolean;
	/** Indicates whether the Pipeline should be included or not. Default true. */
	pipeline?: boolean;
	/** Indicates whether the Code should be included or not. Default true. */
	code?: boolean;
	/** Indicates whether the Name should be included or not. Default true. */
	name?: boolean;
	/** Indicates whether the Description should be included or not. Default true. */
	description?: boolean;
	/** Indicates whether the Status should be included or not. Default true. */
	status?: boolean;
	/** Indicates whether the Parent IP should be included or not. Default false. */
	parent?: boolean | IVIPIpOptions;
	/** Indicates whether the Child IPs should be included or not. Default false. */
	children?: boolean | IVIPIpOptions;
	/** Indicates whether the IPs given by this IP should be included or not. Default false. */
	give?: boolean | IVIPIpEquivalenceOptions;
	/** Indicates whether the IPs giving this IP should be included or not. Default false. */
	given_by?: boolean | IVIPIpEquivalenceOptions;
	/** Indicates whether the Certificates should be included or not. Default false. */
	certificates?: boolean | IVIPCertificateIpOptions;
	/** Indicates whether the Capabilities should be included or not. Default false. */
	capabilities?: boolean | IVIPCapabilityOptions;
}

/**
 * IP Base Class Logic.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export class VIPIpBase implements IVIPIpBase, IModel<IVIPIpBase> {
	/** Id */
	public id!: number;
	/** Parent ID */
	public fk_parent!: number | null;
	/** Exceptional */
	public exceptional!: boolean;
	/** Pipeline */
	public pipeline!: boolean;
	/** Code */
	public code!: number;
	/** Name */
	public name!: string;
	/** Description */
	public description!: string | null;
	/** Status */
	public status!: VipIpStatus;

	/**
	 * Get Parent IP
	 */
	public get parent(): VIPIp | null {
		return this.fk_parent && db.ips.get(this.fk_parent) || null;
	}

	/**
	 * Set Parent IP
	 */
	public set parent(value: VIPIp | null) {
		this.fk_parent = Number.nullable(value?.id);
	}

	/**
	 * Get Child IPs
	 */
	public get children(): VIPIp[] {
		return db.ips.list.filter(x => x.fk_parent === this.id);
	}

	/**
	 * Get IPs given by this IP
	 */
	public get give(): VIPIpEquivalence[] {
		return db.ipEquivalences.list.filter(x => x.fk_ip_parent === this.id);
	}

	/**
	 * Get IPs giving this IP
	 */
	public get given_by(): VIPIpEquivalence[] {
		return db.ipEquivalences.list.filter(x => x.fk_ip === this.id);
	}

	/**
	 * Get Certificates
	 */
	public get certificates(): VIPCertificateIp[] {
		return db.certificateIPS.list.filter(x => x.fk_ip === this.id);
	}

	/**
	 * Get Capabilities
	 */
	public get capabilities(): VIPCapability[] {
		return db.capabilities.list.filter(x => x.fk_ip === this.id);
	}

	/**
	 * Check if this Status is Active.
	 *
	 * @return bool
	 */
	public get isActive(): boolean {
		return this.status === VipIpStatus.active;
	}

	/**
	 * Check if this Status is Disabled.
	 *
	 * @return bool
	 */
	public get isDisabled(): boolean {
		return this.status === VipIpStatus.disabled;
	}

	/**
	 * Get the Status label
	 *
	 * @return string
	 */
	public getStatusLabel(): string {
		return getVipIpStatusLabel(this.status);
	}

	/**
	 * Constructor.
	 * @param data
	 */
	constructor (data: IVIPIpBase = {}) {
		this.load(data);
	}

	/**
	 * Set all properties with the data provided or a default value.
	 *
	 * All properties will be set, either with the value provided or with their default value.
	 *
	 * If you want to set only a subset of this instance's properties, use {@link VIPIpBase.update} instead.
	 *
	 * @param data
	 */
	public load(data: IVIPIpBase) {
		this.id = Number.orDefault(data.id);
		this.fk_parent = Number.nullable(data.fk_parent);
		this.exceptional = Boolean.orDefault(data.exceptional, false);
		this.pipeline = Boolean.orDefault(data.pipeline, false);
		this.code = Number.orDefault(data.code);
		this.name = String.orDefault(data.name);
		this.description = String.nullable(data.description);
		this.status = vipIpStatusOrDefault(data.status, 'active');
		
		if (data.parent) {
			db.ips.insertOrUpdate(data.parent);
		}
		
		if (Array.isArray(data.children) && data.children.length) {
			db.ips.insertOrUpdateMany(data.children);
		}
		if (Array.isArray(data.give) && data.give.length) {
			db.ipEquivalences.insertOrUpdateMany(data.give);
		}
		if (Array.isArray(data.given_by) && data.given_by.length) {
			db.ipEquivalences.insertOrUpdateMany(data.given_by);
		}
		if (Array.isArray(data.certificates) && data.certificates.length) {
			db.certificateIPS.insertOrUpdateMany(data.certificates);
		}
		if (Array.isArray(data.capabilities) && data.capabilities.length) {
			db.capabilities.insertOrUpdateMany(data.capabilities);
		}
	}

	/**
	 * Set the properties with the data provided.
	 *
	 * Only the properties defined in data will be updated. Falsy values will be treated, only `undefined` is not.
	 *
	 * If you want to redefine all properties, use {@link VIPIpBase.load} instead.
	 *
	 * @param data
	 */
	public update(data: IVIPIpBase) {
		if (data.id !== undefined) {
			this.id = Number.orDefault(data.id);
		}
		if (data.fk_parent !== undefined) {
			this.fk_parent = Number.nullable(data.fk_parent);
		}
		if (data.exceptional !== undefined) {
			this.exceptional = Boolean.orDefault(data.exceptional, false);
		}
		if (data.pipeline !== undefined) {
			this.pipeline = Boolean.orDefault(data.pipeline, false);
		}
		if (data.code !== undefined) {
			this.code = Number.orDefault(data.code);
		}
		if (data.name !== undefined) {
			this.name = String.orDefault(data.name);
		}
		if (data.description !== undefined) {
			this.description = String.nullable(data.description);
		}
		if (data.status !== undefined) {
			this.status = vipIpStatusOrDefault(data.status, 'active');
		}
		
		if (data.parent) {
			db.ips.insertOrUpdate(data.parent);
		}
		
		if (Array.isArray(data.children) && data.children.length) {
			db.ips.insertOrUpdateMany(data.children);
		}
		if (Array.isArray(data.give) && data.give.length) {
			db.ipEquivalences.insertOrUpdateMany(data.give);
		}
		if (Array.isArray(data.given_by) && data.given_by.length) {
			db.ipEquivalences.insertOrUpdateMany(data.given_by);
		}
		if (Array.isArray(data.certificates) && data.certificates.length) {
			db.certificateIPS.insertOrUpdateMany(data.certificates);
		}
		if (Array.isArray(data.capabilities) && data.capabilities.length) {
			db.capabilities.insertOrUpdateMany(data.capabilities);
		}
	}
}
